import { type ReactNode, useEffect, useMemo } from 'react';
import { useStepperDialog } from '@ics-portal/guardian';
import { hasDirtyFields } from '@utils/hasDirtyFields';
import {
    type ProjectFormAttributes,
    useProjectFormContext,
} from '../../ProjectForm';
import { ProjectFormDetails } from './ProjectFormDetails';

interface ProjectFormDetailsStepProps {
    isEditMode?: boolean;
}

export function ProjectFormDetailsStep({
    isEditMode,
}: ProjectFormDetailsStepProps): ReactNode {
    const { registerProceed, setPrimaryDisabled } = useStepperDialog();
    const { form } = useProjectFormContext();
    const {
        getValues,
        trigger,
        watch,
        formState: { errors, dirtyFields },
    } = form;

    const detailsFields = useMemo<(keyof ProjectFormAttributes)[]>(
        () => [
            'name',
            'description',
            'typeID',
            'statusID',
            'milestoneID',
            'organizationID',
            'securityResponsibleID',
        ],
        [],
    );

    const values = watch(detailsFields);
    const hasErrors = detailsFields.some((field) => Boolean(errors[field]));
    const allFieldsFilled = detailsFields.every((_, index) =>
        Boolean(values[index]),
    );
    const hasUnsavedChanges = hasDirtyFields(dirtyFields);
    const primaryDisabled =
        hasErrors ||
        !allFieldsFilled ||
        (Boolean(isEditMode) && !hasUnsavedChanges);

    useEffect(() => {
        registerProceed(async () => {
            const isStepValid = await trigger(detailsFields);
            const detailValues = getValues(detailsFields);
            const allDetailsFilled = detailValues.every(Boolean);

            return isStepValid && allDetailsFilled;
        });
    }, [registerProceed, getValues, trigger, detailsFields]);

    useEffect(() => {
        setPrimaryDisabled(primaryDisabled, { isInvalid: hasErrors });
    }, [setPrimaryDisabled, primaryDisabled, hasErrors]);

    return <ProjectFormDetails isEditMode={isEditMode} />;
}
